import './chatbot.css';
import React, { useState, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';

interface Payload {
  inspectionComment: string;
}

interface Message {
  id: string;
  sender: 'user' | 'bot';
  text: string;
}

const API_URL = '/stg/process_comments';

export const ChatBot: React.FC = () => {
  const [form, setForm] = useState<Payload>({
    inspectionComment: '',
  });

  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);

  const appendMessage = (sender: 'user' | 'bot', text: string) => {
    setMessages((prev) => [
      ...prev,
      { id: uuidv4(), sender, text },
    ]);
    setTimeout(() => {
      chatRef.current?.scrollTo({
        top: chatRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }, 100);
  };

  const sendComment = async () => {
    if (!form.inspectionComment.trim()) {
      alert('Inspection comment is required.');
      return;
    }

    appendMessage('user', form.inspectionComment);
    setLoading(true);

    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(form),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Server responded with status ${response.status}`);
      }

      const data = await response.json();

      // Format the response data for display
      let formattedResponse = '';
      if (data.response && Array.isArray(data.response)) {
        data.response.forEach((item: any, index: number) => {
          if (index > 0) formattedResponse += '\n-------------------------------------------------\n';
          formattedResponse += `Service ${index + 1}\n`;
          formattedResponse += '___________________________________________________________________\n'
          formattedResponse += `Pest: ${item.pest}\n`;
          formattedResponse += `Treatment: ${item.treatment}\n`;
          formattedResponse += `Preparation: ${item.preparation}\n`;
          
          if (item.activity && Array.isArray(item.activity) && item.activity.length > 0) {
            formattedResponse += `Activity:\n`;
            item.activity.forEach((activity: string) => {
              formattedResponse += `- ${activity}\n`;
            });
          }

          if(item.sanitation && item.structural){
            formattedResponse += 'Issue:\n'
            formattedResponse += '- Sanitation\n'
            formattedResponse += '- Structural\n'
          }

          else if(item.sanitation){
            formattedResponse += 'Issue:\n'
            formattedResponse += '- Sanitation\n'
          }

          else if(item.structural){
            formattedResponse += 'Issue:\n'
            formattedResponse += '- Structural\n'
          }

          if (item.follow_up && Array.isArray(item.follow_up) && item.follow_up.length > 0) {
            let index = 0
            formattedResponse += `Follow Up:\n`;
            item.follow_up.forEach((followUp: string) => {
              formattedResponse += `- ${followUp} ${item.follow_up_timeperiod[index] || ''}\n`;
              index++
            });
          }

          if(item.reschedule){
            formattedResponse += `${item.reschedule === true ? 'Reschedule: required' : ''}\n`
          }
          
          formattedResponse += `\n${item.processed_comment}\n`;
        });
      } else {
        formattedResponse = 'No response from server.';
      }

      appendMessage('bot', formattedResponse);
    } catch (error: any) {
      appendMessage('bot', `Error: Unable to reach the server. ${error.message || ''}`);
    } finally {
      setLoading(false);
    }

    setForm({ inspectionComment: '' });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const { value } = e.target;
    setForm({ inspectionComment: value });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendComment();
    }
  };

  return (
    <div className="chat-container">
        <div className="chat-box">
          <h2>PestDetect by homepro</h2>

          <form className="form-section" onSubmit={(e) => { e.preventDefault(); sendComment(); }}>
            <textarea
              name="inspectionComment"
              placeholder="Enter inspection comment..."
              value={form.inspectionComment}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              rows={3}
              disabled={loading}
            />
            <button type="submit" disabled={loading}>
              {loading ? <span className="loader"></span> : 'Send'}
            </button>
          </form>

          <div className="messages" ref={chatRef}>
            {messages.map((msg) => (
              <div key={msg.id} className={`message ${msg.sender}`}>
                {msg.text}
              </div>
            ))}
          </div>
        </div>
      </div>
  );
};
